import { useEffect, useState } from "react";

import { twMerge } from "tailwind-merge";

import { formatting } from "@north-beam/nb-common";

import { isNumber } from "./number-input";
import { TextInput, type TextInputProps } from "./text-input";

function formatValue(value: number | undefined, decimalPlaces?: number) {
  return value !== undefined
    ? formatting.formatDecimal(value, { mantissa: decimalPlaces })
    : "";
}

export interface FormattedNumberInputProps
  extends Omit<TextInputProps, "value" | "onChange" | "defaultValue"> {
  value: number | undefined;
  defaultValue?: number;
  onChange: (value: number | undefined) => void;
  decimalPlaces?: number;
}

export const FormattedNumberInput = ({
  className,
  value,
  defaultValue,
  onChange,
  decimalPlaces = 2,
  ...props
}: FormattedNumberInputProps) => {
  const [localValue, setLocalValue] = useState(
    formatValue(value, decimalPlaces),
  );

  useEffect(() => {
    setLocalValue(formatValue(value, decimalPlaces));
  }, [value, decimalPlaces]);

  return (
    <TextInput
      className={twMerge("text-xs text-right font-mono", className)}
      value={localValue}
      onPaste={(e) => {
        e.preventDefault();
        const val = e.clipboardData.getData("text").replaceAll(",", "");

        if (isNumber(val)) {
          setLocalValue(val);
        }
      }}
      onChange={(e) => {
        const val = e.target.value;

        if (!val) {
          setLocalValue("");
        } else if (val === "-") {
          setLocalValue(val);
        } else if (val.startsWith("+") || val.match(/^0\d+/)) {
          // do nothing
        } else if (isNumber(val)) {
          setLocalValue(val);
        }
      }}
      onFocus={() => {
        setLocalValue(localValue.replaceAll(",", ""));
      }}
      onBlur={() => {
        if (localValue) {
          const formattedValue = formatValue(Number(localValue), decimalPlaces);
          onChange(Number(formattedValue.replaceAll(",", "")));
          setLocalValue(formattedValue);
        } else {
          if (defaultValue !== undefined) {
            onChange(defaultValue);
            setLocalValue(formatValue(defaultValue, decimalPlaces));
          } else {
            onChange(undefined);
            setLocalValue("");
          }
        }
      }}
      {...props}
    />
  );
};
